import React, { useMemo, useState } from 'react'
import {
	Box,
	Breadcrumbs,
	ButtonBase,
	Checkbox,
	Collapse,
	Drawer,
	FormControlLabel,
	IconButton,
	Link,
	MenuItem,
	Tab,
	Tabs,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro'
import CustomButton from '../components/v2/general/CustomButton/CustomButton'
import vehiclesMock from '../data/mockedData/schedules/vehicles.json'
import { useNavigate, useParams } from 'react-router-dom'
import { dataGridDensityTransitionSx } from '../components/v2/general/CustomDataGrid/dataGridDensityTransitionSx'
import { useModal } from '../store/ModalProvider'
import type {
	VehicleAccidentsModalData,
	VehicleServiceTotalsModalData,
	VehicleSituationsModalData,
} from '../types/modal'

type DensityMode = 'compact' | 'standard'

interface Vehicle {
	id: number
	code: string
	seats?: number
	status?: string
	type?: string
	situation?: string
	location?: string
}

const breadcrumbLinkSx = {
	fontFamily: 'Poppins',
	fontSize: '11px',
	lineHeight: 1.5,
	color: '#8695AF',
	textDecoration: 'none',
}

const breadcrumbCurrentSx = {
	fontFamily: 'Poppins',
	fontSize: '11px',
	lineHeight: 1.5,
	color: '#00A5FF',
}

const headerFieldSx = {
	'& .MuiInputLabel-root': {
		fontSize: '11px',
		color: '#092056',
		lineHeight: '12px',
		letterSpacing: '0.15px',
		transformOrigin: 'top left',
		backgroundColor: '#FFFFFF',
		padding: '0 4px',
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
	},
}

const cardFieldSx = {
	...headerFieldSx,
	'& .MuiOutlinedInput-root': {
		...headerFieldSx['& .MuiOutlinedInput-root'],
		minHeight: '36px',
	},
}

const sectionTitleSx = {
	fontFamily: 'Poppins',
	fontWeight: 600,
	fontSize: '20px',
	lineHeight: 1.3,
	color: '#092056',
}

const tableDensityToggleSx = {
	border: '1px solid rgba(134, 149, 175, 0.15)',
	borderRadius: '4px',
	'& .MuiToggleButton-root': {
		border: 'none',
		borderRadius: 0,
		padding: '4.8px',
		'&:not(:last-child)': {
			borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
		},
		'&.Mui-selected': {
			backgroundColor: '#F5F7FC',
			'&:hover': {
				backgroundColor: '#F5F7FC',
			},
		},
	},
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '12px',
		color: '#8695AF',
		letterSpacing: '0.17px',
		lineHeight: '24px',
		textTransform: 'none',
	},
	'& .MuiDataGrid-columnSeparator': {
		display: 'none',
	},
	'& .MuiDataGrid-columnHeader': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		color: '#092056',
		borderBottom: 'none',
		borderRight: 'none',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.06)',
	},
	'& .MuiDataGrid-row.Mui-selected': {
		backgroundColor: '#F5F7FC',
	},
	'& .MuiDataGrid-row.Mui-selected:hover': {
		backgroundColor: '#EEF2FA',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const statusChipBaseSx = {
	display: 'inline-flex',
	alignItems: 'center',
	gap: '6px',
	borderRadius: '999px',
	padding: '4px 12px',
	fontFamily: 'Poppins',
	fontSize: '12px',
	fontWeight: 600,
	lineHeight: 1.3,
	border: '1px solid transparent',
}

const escalaRows = [
	{
		id: '1',
		center: 'Escalas Transol',
		type: 'Agências Transol',
		typeId: '3',
		centerId: '1',
		canChange: 'Sim',
		filial: '001',
	},
	{
		id: '2',
		center: 'Escalas Transol',
		type: 'Ocasionais Transol',
		typeId: '2',
		centerId: '1',
		canChange: 'Sim',
		filial: '001',
	},
	{
		id: '3',
		center: 'Escalas Transol',
		type: 'Fixos Transol',
		typeId: '1',
		centerId: '1',
		canChange: 'Não',
		filial: '001',
	},
	{
		id: '4',
		center: 'Follow Me',
		type: 'Fixos Follow Me',
		typeId: '330',
		centerId: '33',
		canChange: 'Sim',
		filial: '033',
	},
]

const SchedulesVehiclePage = React.memo(function SchedulesVehiclePage() {
	const navigate = useNavigate()
	const { id } = useParams()
	const { openModal } = useModal()
	const vehicles = useMemo(() => vehiclesMock as Vehicle[], [])
	const vehicle = vehicles.find(item => String(item.id) === String(id)) ?? vehicles[0]
	const [selectedVehicle, setSelectedVehicle] = useState('001 - 02-HV-26')
	const [tabValue, setTabValue] = useState(0)
	const [tableDensity, setTableDensity] = useState<DensityMode>('compact')
	const [consultDrawerOpen, setConsultDrawerOpen] = useState(false)
	const [consultSectionOpen, setConsultSectionOpen] = useState(true)
	const [inspectionOpen, setInspectionOpen] = useState({
		latest: false,
		all: false,
		maintenance: false,
	})
	const [selectedEscalas, setSelectedEscalas] = useState<string[]>([escalaRows[0]?.id ?? '1', escalaRows[1]?.id ?? '2'])

	const handleOpenVehicleServiceTotals = () => {
		const modalData: VehicleServiceTotalsModalData = {
			vehicleId: vehicle?.id ?? id ?? 1,
			startDate: '02/05/2024',
			endDate: '02/05/2024',
			filial: 'Transol',
			vehicle: '0046',
			includeScheduleTime: true,
			footerVariant: 'export',
			services: [],
			totals: [],
			stats: {
				kmsCarga: '200',
				kmsVazio: '50',
				totalKms: '250',
				duracao: '02:00',
			},
		}

		setConsultDrawerOpen(false)
		openModal('Servicos e Totais por Viatura', modalData, [])
	}

	const handleOpenVehicleSituations = () => {
		const modalData: VehicleSituationsModalData = {
			vehicleId: vehicle?.id ?? id ?? 1,
			vehicle: selectedVehicle,
			startDate: '01/05/2024',
			endDate: '01/09/2024',
			situacao: 'Todas',
			rows: [],
		}

		setConsultDrawerOpen(false)
		openModal('Situações da Viatura', modalData, [])
	}

	const handleOpenVehicleAccidents = () => {
		const modalData: VehicleAccidentsModalData = {
			vehicleId: vehicle?.id ?? id ?? 1,
			startDate: '01/01/2024',
			endDate: '31/12/2024',
			vehicle: selectedVehicle,
			onlyActive: true,
			rows: [],
		}

		setConsultDrawerOpen(false)
		openModal('Sinistralidade da Viatura', modalData, [])
	}

	const handleOpenDriverSheet = () => {
		const selectedVehicleId = selectedVehicle.split(' - ')[0]?.trim()
		const parsedSelectedVehicleId = Number.parseInt(selectedVehicleId, 10)
		const driverId = Number.isNaN(parsedSelectedVehicleId)
			? String(id ?? vehicle?.id ?? 1)
			: String(parsedSelectedVehicleId)
		setConsultDrawerOpen(false)
		navigate(`/schedules/driver/${driverId}`)
	}

	const columns = useMemo<GridColDef[]>(
		() => [
			{
				field: 'center',
				headerName: 'Centro de Escalas',
				minWidth: 180,
				flex: 1,
				sortable: false,
			},
			{ field: 'type', headerName: 'Tipo Escala', width: 160, sortable: false },
			{ field: 'typeId', headerName: 'ID Tipo Escala', width: 120, sortable: false },
			{
				field: 'centerId',
				headerName: 'Id Centro Escala',
				width: 120,
				sortable: false,
			},
			{
				field: 'canChange',
				headerName: 'User Pode Alterar',
				width: 140,
				sortable: false,
			},
			{ field: 'filial', headerName: 'ID Filial', width: 90, sortable: false },
		],
		[],
	)

	return (
		<Box
			sx={{
				position: 'relative',
				display: 'flex',
				flexDirection: 'column',
				width: '100%',
				height: '100%',
				overflow: 'hidden',
				flex: 1,
				minWidth: 0,
				gap: '12px',
			}}
		>
			<Breadcrumbs
				aria-label='breadcrumb'
				separator={<Typography sx={{ ...breadcrumbLinkSx, color: '#C7D1E0' }}>/</Typography>}
				sx={{
					width: '100%',
					height: '40px',
					display: 'flex',
					alignItems: 'center',
					'& .MuiBreadcrumbs-ol': {
						alignItems: 'center',
					},
					'& .MuiBreadcrumbs-separator': {
						margin: '0 6px',
					},
				}}
			>
				<Link
					underline='none'
					href='/'
					sx={breadcrumbLinkSx}
				>
					Home
				</Link>
				<Link
					underline='none'
					href='/schedules'
					sx={breadcrumbLinkSx}
				>
					Escalas
				</Link>
				<Typography sx={breadcrumbCurrentSx}>Ficha Viatura</Typography>
			</Breadcrumbs>

			<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
				<KeyboardArrowLeftRoundedIcon sx={{ color: '#00A5FF' }} />
				<Link
					underline='none'
					href='/schedules'
					sx={breadcrumbCurrentSx}
				>
					Escalas
				</Link>
			</Box>

			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					flexWrap: 'wrap',
					gap: '16px',
				}}
			>
				<Typography
					sx={{
						fontFamily: 'Poppins',
						fontWeight: 600,
						fontSize: '24px',
						color: '#092056',
					}}
				>
					Ficha de Viatura
				</Typography>
			</Box>

			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: '16px',
					flexWrap: 'wrap',
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
					<TextField
						select
						label='Viatura'
						size='small'
						value={selectedVehicle}
						onChange={event => setSelectedVehicle(event.target.value)}
						SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
						sx={{ ...headerFieldSx, minWidth: '180px' }}
					>
						<MenuItem value='001 - 02-HV-26'>001 - 02-HV-26</MenuItem>
						{vehicle && (
							<MenuItem value={`${vehicle.id} - ${vehicle.code}`}>
								{vehicle.id} - {vehicle.code}
							</MenuItem>
						)}
					</TextField>

					<Box
						sx={{
							...statusChipBaseSx,
							color: '#2E7D32',
							borderColor: '#2E7D32',
							backgroundColor: 'rgba(46, 125, 50, 0.08)',
						}}
					>
						<CheckCircleRoundedIcon sx={{ fontSize: 16 }} />
						Ativa
					</Box>
					<Box
						sx={{
							...statusChipBaseSx,
							color: '#FF7A00',
							borderColor: '#FF7A00',
							backgroundColor: 'rgba(255, 122, 0, 0.08)',
						}}
					>
						<WarningAmberRoundedIcon sx={{ fontSize: 16 }} />
						Intervenções Expiradas
					</Box>
					<Box
						sx={{
							...statusChipBaseSx,
							color: '#FF7A00',
							borderColor: '#FF7A00',
							backgroundColor: 'rgba(255, 122, 0, 0.08)',
						}}
					>
						<WarningAmberRoundedIcon sx={{ fontSize: 16 }} />
						Ocorrências - Pode Circular
					</Box>
				</Box>

				<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
					<ButtonBase
						onClick={() => setConsultDrawerOpen(true)}
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '6px',
							color: '#00A5FF',
							padding: '4px 6px',
							borderRadius: '6px',
						}}
					>
						<Typography sx={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: '13px' }}>Consultar</Typography>
						<FilterAltOutlinedIcon sx={{ fontSize: 18 }} />
					</ButtonBase>
					<CustomButton
						size='small'
						sx={{
							background: '#00A5FF',
							color: '#FFFFFF',
							textTransform: 'none',
							borderRadius: '4px',
							padding: '6px 12px',
							fontWeight: 600,
							fontSize: '12px',
							lineHeight: '20px',
						}}
					>
						Ocorrências
					</CustomButton>
					<CustomButton
						size='small'
						sx={{
							background: '#092056',
							color: '#FFFFFF',
							textTransform: 'none',
							borderRadius: '4px',
							padding: '6px 12px',
							fontWeight: 600,
							fontSize: '12px',
							lineHeight: '20px',
						}}
					>
						Guardar
					</CustomButton>
				</Box>
			</Box>

			<Box
				sx={{
					flex: 1,
					backgroundColor: '#FFFFFF',
					borderRadius: '12px',
					padding: '24px',
					overflowY: 'auto',
				}}
			>
				<Tabs
					value={tabValue}
					onChange={(_, value) => setTabValue(value)}
					centered
					TabIndicatorProps={{
						style: {
							backgroundColor: '#00A5FF',
							height: '3px',
							borderRadius: '2px',
						},
					}}
					sx={{
						minHeight: '40px',
						'& .MuiTab-root': {
							fontSize: '13px',
							fontWeight: 600,
							textTransform: 'none',
							minHeight: '40px',
							padding: '6px 16px',
							color: '#8695AF',
						},
						'& .MuiTab-root.Mui-selected': {
							color: '#00A5FF',
						},
					}}
				>
					<Tab label='Dados Gerais' />
					<Tab label='Características' />
					<Tab label='Inspeções, Intervenções e Manutenção' />
				</Tabs>

				{tabValue === 0 && (
					<Box sx={{ paddingTop: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
							<Typography sx={sectionTitleSx}>Dados da Viatura</Typography>
							<FormControlLabel
								control={<Checkbox size='small' />}
								label='Viatura Virtual (apenas para chapas)'
								sx={{
									margin: 0,
									'.MuiTypography-root': {
										fontFamily: 'Poppins',
										fontSize: '12px',
										color: '#092056',
										fontWeight: 500,
									},
								}}
							/>
						</Box>

						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
								gap: '24px',
							}}
						>
							<Box sx={{ display: 'grid', gap: '16px' }}>
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
										gap: '12px',
									}}
								>
									<TextField
										label='Nr Frota'
										size='small'
										value='001'
										sx={cardFieldSx}
									/>
									<TextField
										label='Matrícula'
										size='small'
										value='02-HV-26'
										sx={cardFieldSx}
									/>
									<TextField
										label='Data Matrícula'
										size='small'
										value='05/07/1979'
										sx={cardFieldSx}
									/>
									<TextField
										label='Kms Atuais'
										size='small'
										value='990000'
										sx={cardFieldSx}
									/>
									<TextField
										label='Marca Viatura'
										size='small'
										value='Mercedes - Travego'
										sx={cardFieldSx}
									/>
									<TextField
										select
										label='Tipologia'
										size='small'
										value='Bus'
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
										sx={cardFieldSx}
									>
										<MenuItem value='Bus'>Bus</MenuItem>
									</TextField>
									<TextField
										label='Pisos'
										size='small'
										value='1'
										sx={cardFieldSx}
									/>
									<TextField
										label='Eixos'
										size='small'
										value='2'
										sx={cardFieldSx}
									/>
									<TextField
										label='Lotação'
										size='small'
										value='49'
										sx={cardFieldSx}
									/>
									<TextField
										select
										label='Tipo Inspeções'
										size='small'
										value='Pesados Passageiros'
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
										sx={cardFieldSx}
									>
										<MenuItem value='Pesados Passageiros'>Pesados Passageiros</MenuItem>
									</TextField>
									<TextField
										label='Cor Viatura'
										size='small'
										value='Sem cor definida'
										sx={cardFieldSx}
									/>
								</Box>
								<TextField
									label='Comentários e Observações'
									size='small'
									value='It is a long established fact that a reader will be distracted by the readable content of a page when looking at its layout.'
									sx={cardFieldSx}
									multiline
									minRows={3}
								/>
							</Box>

							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
									<Typography sx={{ ...sectionTitleSx, fontSize: '14px' }}>Proprietário e Utilizador</Typography>
									<TextField
										select
										label='Proprietário'
										size='small'
										value='Transol'
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
										sx={cardFieldSx}
									>
										<MenuItem value='Transol'>Transol</MenuItem>
									</TextField>
									<TextField
										select
										label='Utilizador'
										size='small'
										value='Transol'
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
										sx={cardFieldSx}
									>
										<MenuItem value='Transol'>Transol</MenuItem>
									</TextField>
								</Box>

								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
									<Typography sx={{ ...sectionTitleSx, fontSize: '14px' }}>Local de Pernoita / Motorista</Typography>
									<TextField
										select
										label='Local'
										size='small'
										value='Portimão (Oficina)'
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
										sx={cardFieldSx}
									>
										<MenuItem value='Portimão (Oficina)'>Portimão (Oficina)</MenuItem>
									</TextField>
									<TextField
										select
										label='Zona'
										size='small'
										value='Portimão'
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
										sx={cardFieldSx}
									>
										<MenuItem value='Portimão'>Portimão</MenuItem>
									</TextField>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
										<TextField
											label='Motorista'
											size='small'
											value='489 - Z-Aldina'
											sx={{ ...cardFieldSx, flex: 1 }}
										/>
										<CustomButton
											size='small'
											sx={{
												background: '#00A5FF',
												color: '#FFFFFF',
												textTransform: 'none',
												borderRadius: '4px',
												padding: '6px 12px',
												fontWeight: 600,
												fontSize: '12px',
												lineHeight: '20px',
											}}
										>
											Ficha Motorista
										</CustomButton>
									</Box>
								</Box>
							</Box>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: '12px',
								}}
							>
								<Typography sx={{ ...sectionTitleSx, fontSize: '20px' }}>Escalas em que participa</Typography>
								<ToggleButtonGroup
									value={tableDensity}
									exclusive
									onChange={(_, value) => value && setTableDensity(value)}
									size='small'
									sx={tableDensityToggleSx}
								>
									<ToggleButton
										value='compact'
										aria-label='densidade compacta'
									>
										<TableRowsIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: tableDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='densidade espaçada'
									>
										<ViewHeadlineIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: tableDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>

							<Box
								sx={{
									border: '1px solid rgba(134, 149, 175, 0.15)',
									borderRadius: '6px',
									overflow: 'hidden',
								}}
							>
								<DataGridPro
									rows={escalaRows}
									columns={columns}
									density={tableDensity}
									rowHeight={tableDensity === 'compact' ? 32 : 40}
									columnHeaderHeight={40}
									autoHeight
									checkboxSelection
									disableRowSelectionOnClick
									disableColumnMenu
									disableColumnFilter
									disableColumnSelector
									disableColumnReorder
									hideFooter
									rowSelectionModel={selectedEscalas}
									onRowSelectionModelChange={selection => setSelectedEscalas(selection as string[])}
									sx={[tableSx, dataGridDensityTransitionSx]}
								/>
							</Box>
						</Box>
					</Box>
				)}

				{tabValue === 1 && (
					<Box sx={{ paddingTop: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
						<Typography sx={sectionTitleSx}>Características da Viatura</Typography>

						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
								gap: '24px',
								alignItems: 'start',
							}}
						>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
								{[
									{ label: 'Via Verde', checked: true },
									{ label: 'Possui Wi-Fi', checked: true },
									{ label: 'Lotação Virtual', checked: true },
									{ label: 'Tacógrafo Digital', checked: false },
									{ label: "Leitor de DVD's", checked: false },
									{ label: 'Casa de Banho (WC)', checked: false },
									{ label: 'Cintos de 3 Pontos', checked: false },
									{ label: 'Navegação por GPS', checked: false },
								].map(item => (
									<FormControlLabel
										key={item.label}
										control={
											<Checkbox
												size='small'
												defaultChecked={item.checked}
											/>
										}
										label={item.label}
										sx={{
											margin: 0,
											'.MuiTypography-root': {
												fontFamily: 'Poppins',
												fontSize: '12px',
												color: '#092056',
												fontWeight: 500,
											},
										}}
									/>
								))}
							</Box>

							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
								<TextField
									label='Nr Identificador'
									size='small'
									value='8765456782132'
									sx={{ ...cardFieldSx, maxWidth: '220px' }}
								/>
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
										gap: '12px',
									}}
								>
									<TextField
										label='Tipo'
										size='small'
										value='00'
										sx={cardFieldSx}
									/>
									<TextField
										label='SIM'
										size='small'
										value='0000000000'
										sx={cardFieldSx}
									/>
									<TextField
										label='Número'
										size='small'
										value='01000000'
										sx={cardFieldSx}
									/>
								</Box>
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
										gap: '12px',
										maxWidth: '220px',
									}}
								>
									<TextField
										label='Tipo'
										size='small'
										value='00'
										sx={cardFieldSx}
									/>
									<TextField
										label='Ordem'
										size='small'
										value='00'
										sx={cardFieldSx}
									/>
								</Box>
							</Box>

							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
								{[
									'Ar Condicionado',
									'Expresso Alta Qualidade',
									'Pintura (Rede Expressos)',
									'Licença de Transporte',
									'Licença Comunidade Europeia',
									'Pode Transportar Crianças',
									'Viatura Não Escalável',
									'Viatura sem Documentos',
									'Viatura TML',
									'Georeferenciável',
								].map(label => (
									<FormControlLabel
										key={label}
										control={<Checkbox size='small' />}
										label={label}
										sx={{
											margin: 0,
											'.MuiTypography-root': {
												fontFamily: 'Poppins',
												fontSize: '12px',
												color: '#092056',
												fontWeight: 500,
											},
										}}
									/>
								))}
							</Box>
						</Box>
					</Box>
				)}

				{tabValue === 2 && (
					<Box sx={{ paddingTop: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
						<Typography sx={sectionTitleSx}>Inspeções, Intervenções e Manutenção</Typography>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '10px',
								padding: '8px 12px',
								borderRadius: '6px',
								background: 'linear-gradient(90deg, #F77A1E 0%, #F4B04A 100%)',
								color: '#FFFFFF',
							}}
						>
							<WarningAmberRoundedIcon sx={{ fontSize: 18 }} />
							<Typography
								sx={{
									fontFamily: 'Poppins',
									fontSize: '12px',
									fontWeight: 600,
								}}
							>
								Existem intervenções com validade expirada.
							</Typography>
						</Box>

						<FormControlLabel
							control={<Checkbox size='small' />}
							label='Manutenção da Viatura controlada pelo Fabricante/Vendedor'
							sx={{
								margin: 0,
								'.MuiTypography-root': {
									fontFamily: 'Poppins',
									fontSize: '12px',
									color: '#092056',
									fontWeight: 500,
								},
							}}
						/>

						<Box
							sx={{
								borderTop: '1px solid rgba(134, 149, 175, 0.15)',
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							{[
								{ key: 'latest', label: 'Últimos Registos de Intervenção' },
								{ key: 'all', label: 'Todas as Intervenções' },
								{ key: 'maintenance', label: 'Manutenção' },
							].map(item => (
								<Box key={item.key}>
									<ButtonBase
										onClick={() =>
											setInspectionOpen(prev => ({
												...prev,
												[item.key]: !prev[item.key as keyof typeof prev],
											}))
										}
										sx={{
											width: '100%',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											padding: '12px 4px',
											borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
										}}
									>
										<Typography
											sx={{
												fontFamily: 'Poppins',
												fontSize: '12px',
												fontWeight: 600,
												color: '#092056',
											}}
										>
											{item.label}
										</Typography>
										<KeyboardArrowDownIcon
											sx={{
												color: '#00A5FF',
												transform: inspectionOpen[item.key as keyof typeof inspectionOpen]
													? 'rotate(180deg)'
													: 'rotate(0deg)',
												transition: 'transform 0.2s ease',
											}}
										/>
									</ButtonBase>
									{item.key === 'latest' && (
										<Collapse
											in={inspectionOpen.latest}
											timeout={200}
											unmountOnExit
										>
											<Box
												sx={{
													padding: '16px 4px 24px',
													display: 'flex',
													flexDirection: 'column',
													gap: '16px',
												}}
											>
												<Box
													sx={{
														display: 'flex',
														justifyContent: 'space-between',
														alignItems: 'center',
														gap: '16px',
														flexWrap: 'wrap',
													}}
												>
													<Typography
														sx={{
															fontFamily: 'Poppins',
															fontWeight: 600,
															fontSize: '12px',
															color: '#092056',
														}}
													>
														Se a manutenção não for na filial de origem, indique qual:
													</Typography>
													<TextField
														select
														label='Filial'
														size='small'
														value='Filial a que pertence'
														SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
														sx={{ ...cardFieldSx, minWidth: '180px' }}
													>
														<MenuItem value='Filial a que pertence'>Filial a que pertence</MenuItem>
													</TextField>
												</Box>

												<Box
													sx={{
														display: 'grid',
														gridTemplateColumns: 'minmax(200px, 1fr) minmax(260px, 360px)',
														gap: '12px 24px',
													}}
												>
													{[
														{ label: 'Inspeção', dateLabel: 'Última Inspeção', hasKms: true },
														{
															label: 'Inspeção Transporte de Crianças',
															dateLabel: 'Última Inspeção',
															hasKms: true,
														},
														{
															label: 'Aferição do Tacógrafo',
															dateLabel: 'Última Inspeção',
															hasKms: true,
														},
														{
															label: 'Aferição do Limitador',
															dateLabel: 'Última Inspeção',
															hasKms: true,
														},
														{
															label: 'Validade dos Extintores',
															dateLabel: 'Validade',
															hasKms: false,
															highlight: true,
														},
														{
															label: 'Aferição do Limitador',
															dateLabel: 'Validade',
															hasKms: false,
														},
														{ label: 'Mudança de Óleo', dateLabel: 'Última Inspeção', hasKms: true },
														{ label: 'Lubrificação', dateLabel: 'Última Inspeção', hasKms: true },
														{
															label: 'Mudança Caixa de Velocidades',
															dateLabel: 'Última Inspeção',
															hasKms: true,
														},
														{
															label: 'Mudança de Diferencial',
															dateLabel: 'Última Inspeção',
															hasKms: true,
														},
													].map(row => (
														<React.Fragment key={row.label + row.dateLabel}>
															<Typography
																sx={{
																	fontFamily: 'Poppins',
																	fontWeight: 600,
																	fontSize: '12px',
																	color: '#092056',
																	alignSelf: 'center',
																}}
															>
																{row.label}
															</Typography>
															<Box
																sx={{
																	display: 'grid',
																	gridTemplateColumns: row.hasKms
																		? 'minmax(160px, 1fr) minmax(120px, 1fr)'
																		: 'minmax(160px, 1fr)',
																	gap: '12px',
																}}
															>
																<TextField
																	select
																	label={row.dateLabel}
																	size='small'
																	value='01/03/2023'
																	SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
																	sx={{
																		...cardFieldSx,
																		...(row.highlight
																			? {
																					'& .MuiOutlinedInput-root fieldset': {
																						borderColor: '#F77A1E',
																					},
																				}
																			: {}),
																	}}
																>
																	<MenuItem value='01/03/2023'>01/03/2023</MenuItem>
																</TextField>
																{row.hasKms && (
																	<TextField
																		select
																		label='Kms'
																		size='small'
																		value='500'
																		SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
																		sx={cardFieldSx}
																	>
																		<MenuItem value='500'>500</MenuItem>
																	</TextField>
																)}
															</Box>
														</React.Fragment>
													))}
												</Box>
											</Box>
										</Collapse>
									)}
									{item.key === 'all' && (
										<Collapse
											in={inspectionOpen.all}
											timeout={200}
											unmountOnExit
										>
											<Box
												sx={{
													padding: '16px 4px 24px',
													display: 'flex',
													flexDirection: 'column',
													gap: '16px',
												}}
											>
												<Box
													sx={{
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'space-between',
														gap: '16px',
														flexWrap: 'wrap',
													}}
												>
													<Typography
														sx={{
															fontFamily: 'Poppins',
															fontWeight: 600,
															fontSize: '20px',
															color: '#092056',
														}}
													>
														2 Intervenções
													</Typography>
													<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
														<CustomButton
															variant='text'
															size='small'
															endIcon={<TableRowsIcon sx={{ fontSize: 16 }} />}
															sx={{
																color: '#00A5FF',
																fontWeight: 600,
																fontSize: '12px',
																textTransform: 'none',
																padding: '4px 6px',
															}}
														>
															Adicionar Intervenção
														</CustomButton>
														<CustomButton
															variant='text'
															size='small'
															sx={{
																color: '#00A5FF',
																fontWeight: 600,
																fontSize: '12px',
																textTransform: 'none',
																padding: '4px 6px',
															}}
														>
															Exportar
														</CustomButton>
														<Box
															sx={{
																display: 'flex',
																alignItems: 'center',
																gap: '4px',
																border: '1px solid rgba(134, 149, 175, 0.15)',
																borderRadius: '4px',
																padding: '2px',
															}}
														>
															<Box
																sx={{
																	width: 14,
																	height: 14,
																	backgroundColor: '#00A5FF',
																	borderRadius: '2px',
																}}
															/>
															<Box
																sx={{
																	width: 14,
																	height: 14,
																	backgroundColor: '#E6EDF9',
																	borderRadius: '2px',
																}}
															/>
														</Box>
													</Box>
												</Box>

												<DataGridPro
													rows={[
														{
															id: '1',
															tipo: 'Inspeções Tacógrafo',
															dataIntervencao: '02/05/2024',
															dataRegisto: '02/05/2024',
															validade: '02/05/2025',
															kms: '99000',
															utilizador: 'João',
															utilizadorWindows: 'Sim',
															filial: 'Transol',
															qtd: '1',
															comentarios: 'Lorem Ipsum',
														},
														{
															id: '2',
															tipo: 'Óleo',
															dataIntervencao: '02/05/2024',
															dataRegisto: '02/05/2024',
															validade: '02/05/2025',
															kms: '99000',
															utilizador: 'João',
															utilizadorWindows: 'Sim',
															filial: 'Transol',
															qtd: '1',
															comentarios: 'Lorem Ipsum',
														},
													]}
													columns={[
														{ field: 'tipo', headerName: 'Tipo', minWidth: 180, flex: 1 },
														{ field: 'dataIntervencao', headerName: 'Data Intervenção', width: 120 },
														{ field: 'dataRegisto', headerName: 'Data Registo', width: 110 },
														{ field: 'validade', headerName: 'Validade', width: 110 },
														{ field: 'kms', headerName: 'Kms', width: 80 },
														{ field: 'utilizador', headerName: 'Utilizador', width: 100 },
														{ field: 'utilizadorWindows', headerName: 'Utilizador Windows', width: 140 },
														{ field: 'filial', headerName: 'Filial', width: 100 },
														{ field: 'qtd', headerName: 'Qtd', width: 70 },
														{ field: 'comentarios', headerName: 'Comentários', minWidth: 140, flex: 1 },
													]}
													density='compact'
													rowHeight={32}
													columnHeaderHeight={40}
													autoHeight
													disableRowSelectionOnClick
													disableColumnMenu
													disableColumnFilter
													disableColumnSelector
													disableColumnReorder
													hideFooter
													sx={[tableSx, dataGridDensityTransitionSx]}
												/>
											</Box>
										</Collapse>
									)}
									{item.key === 'maintenance' && (
										<Collapse
											in={inspectionOpen.maintenance}
											timeout={200}
											unmountOnExit
										>
											<Box
												sx={{
													padding: '16px 4px 24px',
													display: 'flex',
													flexDirection: 'column',
													gap: '16px',
												}}
											>
												<Box
													sx={{
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'space-between',
														gap: '16px',
														flexWrap: 'wrap',
													}}
												>
													<Typography
														sx={{
															fontFamily: 'Poppins',
															fontWeight: 600,
															fontSize: '20px',
															color: '#092056',
														}}
													>
														50 Items de Manutenção
													</Typography>
													<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
														<CustomButton
															variant='text'
															size='small'
															endIcon={<TableRowsIcon sx={{ fontSize: 16 }} />}
															sx={{
																color: '#00A5FF',
																fontWeight: 600,
																fontSize: '12px',
																textTransform: 'none',
																padding: '4px 6px',
															}}
														>
															Adicionar Intervenção
														</CustomButton>
														<CustomButton
															variant='text'
															size='small'
															sx={{
																color: '#00A5FF',
																fontWeight: 600,
																fontSize: '12px',
																textTransform: 'none',
																padding: '4px 6px',
															}}
														>
															Exportar
														</CustomButton>
														<Box
															sx={{
																display: 'flex',
																alignItems: 'center',
																gap: '4px',
																border: '1px solid rgba(134, 149, 175, 0.15)',
																borderRadius: '4px',
																padding: '2px',
															}}
														>
															<Box
																sx={{
																	width: 14,
																	height: 14,
																	backgroundColor: '#00A5FF',
																	borderRadius: '2px',
																}}
															/>
															<Box
																sx={{
																	width: 14,
																	height: 14,
																	backgroundColor: '#E6EDF9',
																	borderRadius: '2px',
																}}
															/>
														</Box>
													</Box>
												</Box>

												<DataGridPro
													rows={[
														{
															id: '1',
															tipo: 'Lavagem de Estrada',
															diasIntervalo: '365',
															kmsIntervalo: '0',
															dataUltima: '02/05/2025',
															kmsUltima: '1900',
															dataProxima: '02/05/2026',
															observacoes: 'Lorem Ipsum',
														},
														{
															id: '2',
															tipo: 'Limpeza Filtro de Ar',
															diasIntervalo: '365',
															kmsIntervalo: '0',
															dataUltima: '02/05/2025',
															kmsUltima: '1900',
															dataProxima: '02/05/2026',
															observacoes: 'Lorem Ipsum',
														},
														{
															id: '3',
															tipo: 'Mudança Óleo dos Cubos',
															diasIntervalo: '365',
															kmsIntervalo: '0',
															dataUltima: '02/05/2025',
															kmsUltima: '1900',
															dataProxima: '02/05/2026',
															observacoes: 'Lorem Ipsum',
														},
														{
															id: '4',
															tipo: 'Verificação Óleo travões/embreagem',
															diasIntervalo: '365',
															kmsIntervalo: '0',
															dataUltima: '02/05/2025',
															kmsUltima: '1900',
															dataProxima: '02/05/2026',
															observacoes: 'Lorem Ipsum',
														},
														{
															id: '5',
															tipo: 'Mudança/testo óleo Direção Assistida',
															diasIntervalo: '365',
															kmsIntervalo: '0',
															dataUltima: '02/05/2025',
															kmsUltima: '1900',
															dataProxima: '02/05/2026',
															observacoes: 'Lorem Ipsum',
														},
														{
															id: '6',
															tipo: 'Mudança do filtro do retarder',
															diasIntervalo: '365',
															kmsIntervalo: '0',
															dataUltima: '02/05/2025',
															kmsUltima: '1900',
															dataProxima: '02/05/2026',
															observacoes: 'Lorem Ipsum',
														},
														{
															id: '7',
															tipo: 'Atestos Óleo (motor)',
															diasIntervalo: '365',
															kmsIntervalo: '0',
															dataUltima: '02/05/2025',
															kmsUltima: '1900',
															dataProxima: '02/05/2026',
															observacoes: 'Lorem Ipsum',
														},
														{
															id: '8',
															tipo: 'Mudança Filtro do diferencial',
															diasIntervalo: '365',
															kmsIntervalo: '0',
															dataUltima: '02/05/2025',
															kmsUltima: '1900',
															dataProxima: '02/05/2026',
															observacoes: 'Lorem Ipsum',
														},
														{
															id: '9',
															tipo: 'Mudança Filtro válvulina',
															diasIntervalo: '365',
															kmsIntervalo: '0',
															dataUltima: '02/05/2025',
															kmsUltima: '1900',
															dataProxima: '02/05/2026',
															observacoes: 'Lorem Ipsum',
														},
														{
															id: '10',
															tipo: 'Mudança Óleo da ventoinha',
															diasIntervalo: '365',
															kmsIntervalo: '0',
															dataUltima: '02/05/2025',
															kmsUltima: '1900',
															dataProxima: '02/05/2026',
															observacoes: 'Lorem Ipsum',
														},
													]}
													columns={[
														{ field: 'tipo', headerName: 'Tipo', minWidth: 220, flex: 1 },
														{ field: 'diasIntervalo', headerName: 'Dias Intervalo', width: 120 },
														{ field: 'kmsIntervalo', headerName: 'Kms Intervalo', width: 120 },
														{ field: 'dataUltima', headerName: 'Data Última Manut.', width: 140 },
														{ field: 'kmsUltima', headerName: 'Kms Última Manut.', width: 140 },
														{ field: 'dataProxima', headerName: 'Data Prev. Prox. Manut.', width: 160 },
														{ field: 'observacoes', headerName: 'Observações', minWidth: 140, flex: 1 },
													]}
													density='compact'
													rowHeight={32}
													columnHeaderHeight={40}
													autoHeight
													disableRowSelectionOnClick
													disableColumnMenu
													disableColumnFilter
													disableColumnSelector
													disableColumnReorder
													hideFooter
													sx={[tableSx, dataGridDensityTransitionSx]}
												/>

												<Box
													sx={{
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'flex-end',
														gap: '26px',
														marginTop: '-8px',
													}}
												>
													<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
														<Typography
															sx={{
																fontSize: '11px',
																fontWeight: 400,
																color: '#8695AF',
																lineHeight: 1.3,
															}}
														>
															Linhas por página
														</Typography>
														<Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
															<Typography
																sx={{
																	fontSize: '11px',
																	fontWeight: 400,
																	color: '#092056',
																	lineHeight: 1.3,
																}}
															>
																10
															</Typography>
															<KeyboardArrowDownIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
														</Box>
													</Box>
													<Typography
														sx={{
															fontSize: '11px',
															fontWeight: 400,
															color: '#092056',
															lineHeight: 1.3,
														}}
													>
														1-5 de 50
													</Typography>
													<Box sx={{ display: 'flex', alignItems: 'center' }}>
														<CustomButton
															variant='text'
															sx={{ minWidth: 'auto', padding: '4px' }}
														>
															<KeyboardArrowLeftRoundedIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
														</CustomButton>
														<CustomButton
															variant='text'
															sx={{ minWidth: 'auto', padding: '4px' }}
														>
															<KeyboardArrowRightRoundedIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
														</CustomButton>
													</Box>
												</Box>
											</Box>
										</Collapse>
									)}
								</Box>
							))}
						</Box>
					</Box>
				)}
			</Box>

			<Drawer
				anchor='left'
				open={consultDrawerOpen}
				onClose={() => setConsultDrawerOpen(false)}
				PaperProps={{
					sx: {
						width: { xs: '100%', sm: 360 },
						padding: '20px 16px',
						display: 'flex',
						flexDirection: 'column',
						gap: '16px',
					},
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<Typography
						sx={{
							fontFamily: 'Poppins',
							fontWeight: 600,
							fontSize: '16px',
							lineHeight: 1.75,
							letterSpacing: '0.15px',
							color: '#8695AF',
						}}
					>
						Consultas da Viatura
					</Typography>
					<IconButton
						onClick={() => setConsultDrawerOpen(false)}
						size='small'
						sx={{
							width: 32,
							height: 32,
							borderRadius: '999px',
							border: '1px solid rgba(0, 165, 255, 0.2)',
						}}
					>
						<CloseRoundedIcon sx={{ color: '#00A5FF', fontSize: 18 }} />
					</IconButton>
				</Box>

				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
					<ButtonBase
						onClick={() => setConsultSectionOpen(prev => !prev)}
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							padding: '8px 4px',
						}}
					>
						<Typography
							sx={{
								fontFamily: 'Poppins',
								fontSize: '16px',
								fontWeight: 600,
								color: '#092056',
							}}
						>
							Consultar
						</Typography>
						<KeyboardArrowUpRoundedIcon
							sx={{
								color: '#00A5FF',
								transform: consultSectionOpen ? 'rotate(0deg)' : 'rotate(180deg)',
								transition: 'transform 0.2s ease',
							}}
						/>
					</ButtonBase>

					<Collapse
						in={consultSectionOpen}
						timeout={200}
						unmountOnExit
					>
						<Box sx={{ display: 'flex', flexDirection: 'column' }}>
							{[
								{
									id: 'services-totals',
									label: 'Serviços e Totais da Viatura',
									onClick: handleOpenVehicleServiceTotals,
								},
								{
									id: 'vehicle-situations',
									label: 'Situações da Viatura',
									onClick: handleOpenVehicleSituations,
								},
								{
									id: 'accidents',
									label: 'Sinistralidade',
									onClick: handleOpenVehicleAccidents,
								},
								{ id: 'fueling', label: 'Abastecimentos' },
								{ id: 'oil-changes', label: 'Mudas de Óleo' },
								{
									id: 'driver-sheet',
									label: 'Ficha do Motorista',
									onClick: handleOpenDriverSheet,
								},
							].map(item => (
								<ButtonBase
									key={item.id}
									onClick={item.onClick}
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										padding: '12px 4px',
										borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
									}}
								>
									<Typography
										sx={{
											fontFamily: 'Poppins',
											fontSize: '12px',
											fontWeight: 600,
											color: '#092056',
										}}
									>
										{item.label}
									</Typography>
									<ChevronRightRoundedIcon sx={{ color: '#00A5FF', fontSize: 18 }} />
								</ButtonBase>
							))}
						</Box>
					</Collapse>
				</Box>
			</Drawer>
		</Box>
	)
})

export default SchedulesVehiclePage
